export type ColumnValue = {
  accessorKey: string;
  header: string;
  labelIcon?: string;
  hideHeaderText?: boolean;
  [key: string]: unknown;
};

export type SelectableColumn = {
  label: string;
  value: ColumnValue;
  blocked?: boolean;
  selected: boolean;
};

export type AdditionalColumn = {
  accessorKey: string;
  header: string;
  selectable?: boolean;
  [key: string]: unknown;
};

export const useTableColumn = function (initialColumns: SelectableColumn[], storageKey: string) {
  const selectableColumns = ref<SelectableColumn[]>(initialColumns);

  const tableKey = computed(() =>
    selectableColumns.value
      .filter((c) => c.selected)
      .map((c) => c.label)
      .join("-")
  );

  const tableColumns = computed(() =>
    selectableColumns.value.filter((column) => column.selected).map((column) => column.value)
  );

  const updateColumn = (index: number, selected: boolean) => {
    if (selectableColumns.value[index]) {
      selectableColumns.value[index].selected = selected;
    }
  };

  onMounted(() => {
    const savedColumnsJSON = localStorage.getItem(storageKey);
    if (!savedColumnsJSON) {
      return;
    }
    try {
      const savedColumns = JSON.parse(savedColumnsJSON) as Array<{ label: string; selected: boolean }>;
      const defaultLabels = selectableColumns.value.map((c) => c.label);
      const savedLabels = savedColumns.map((c) => c.label);

      const areColumnsSynced =
        defaultLabels.length === savedLabels.length && defaultLabels.every((label) => savedLabels.includes(label));

      if (!areColumnsSynced) {
        localStorage.removeItem(storageKey);
      } else {
        const selectionMap = new Map(savedColumns.map((c) => [c.label, c.selected]));

        for (const column of selectableColumns.value) {
          const savedState = selectionMap.get(column.label);
          if (savedState !== undefined && !column.blocked) {
            column.selected = savedState;
          }
        }
      }
    } catch (e) {
      console.error("Error parsing stored column preferences:", e);
      localStorage.removeItem(storageKey);
    }
  });

  watch(
    selectableColumns,
    (columns) => {
      const simplifiedColumns = columns.map(({ label, selected }) => ({
        label,
        selected,
      }));
      localStorage.setItem(storageKey, JSON.stringify(simplifiedColumns));
    },
    { deep: true }
  );

  return {
    selectableColumns,
    tableKey,
    tableColumns,
    updateColumn,
  };
};
